import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useJobProgress } from "@/hooks/useJobProgress";
// ---------------------------------------------------------------------------
// Single backup progress card
// ---------------------------------------------------------------------------
function BackupProgressCard({ backupId }) {
    const { data: backup } = useQuery({
        queryKey: ["backup", backupId],
        queryFn: async () => (await api.get(`/backups/${backupId}`)).data,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            return status === "running" || status === "queued" ? 3000 : false;
        },
    });
    const { data: jobData } = useQuery({
        queryKey: ["backup-job", backupId],
        queryFn: async () => (await api.get(`/backups/${backupId}/job`)).data,
        enabled: !!backup,
    });
    const progress = useJobProgress(jobData?.id);
    const pct = progress?.progress_pct ?? (backup?.status === "sealed" ? 100 : 0);
    const state = progress?.state ?? backup?.status ?? "queued";
    const statusIcon = state === "succeeded" || backup?.status === "sealed"
        ? "✅"
        : state === "failed" || backup?.status === "failed"
            ? "❌"
            : "⏳";
    const statusLabel = backup?.status === "sealed"
        ? "sealed"
        : backup?.status === "failed"
            ? "failed"
            : state === "running"
                ? "running"
                : "queued";
    return (_jsxs("div", { className: "flex items-center gap-4 py-3 border-b last:border-0", children: [_jsx("div", { className: "w-48 truncate text-sm text-slate-700", children: backup?.target_email ?? backupId }), _jsx("div", { className: "flex-1", children: _jsx("div", { className: "w-full bg-slate-200 rounded-full h-2", children: _jsx("div", { className: `h-2 rounded-full transition-all ${statusLabel === "failed" ? "bg-red-500" : "bg-brand-500"}`, style: { width: `${pct}%` } }) }) }), _jsxs("div", { className: "w-12 text-right text-xs text-slate-500", children: [pct, "%"] }), _jsxs("div", { className: "w-24 text-right text-sm", children: [statusIcon, " ", statusLabel] })] }));
}
// ---------------------------------------------------------------------------
// Summary counter
// ---------------------------------------------------------------------------
function useSummary(backupIds) {
    const queries = backupIds.map((id) => 
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery({
        queryKey: ["backup", id],
        queryFn: async () => (await api.get(`/backups/${id}`)).data,
        refetchInterval: 3000,
    }));
    return useMemo(() => {
        let completed = 0;
        let failed = 0;
        let running = 0;
        let queued = 0;
        for (const q of queries) {
            const s = q.data?.status;
            if (s === "sealed")
                completed++;
            else if (s === "failed")
                failed++;
            else if (s === "running")
                running++;
            else
                queued++;
        }
        const allDone = completed + failed === backupIds.length;
        return { completed, failed, running, queued, allDone };
    }, [queries, backupIds.length]);
}
// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function BulkProgressPage() {
    const [searchParams] = useSearchParams();
    const backupIds = (searchParams.get("backupIds") ?? "")
        .split(",")
        .filter(Boolean);
    const { completed, failed, running, queued, allDone } = useSummary(backupIds);
    if (backupIds.length === 0) {
        return (_jsxs("div", { className: "p-6", children: [_jsx("p", { className: "text-slate-500", children: "No backups to track." }), _jsx(Link, { to: "/employees", className: "text-brand-600 text-sm mt-2 inline-block", children: "\u2190 Back to Employees" })] }));
    }
    return (_jsxs("div", { className: "max-w-3xl", children: [_jsx("h2", { className: "text-xl font-semibold tracking-tight mb-2", children: "Bulk Backup Progress" }), _jsxs("div", { className: "flex gap-4 text-sm text-slate-600 mb-6", children: [completed > 0 && _jsxs("span", { className: "text-emerald-600 font-medium", children: [completed, " completed"] }), running > 0 && _jsxs("span", { className: "text-brand-600", children: [running, " running"] }), queued > 0 && _jsxs("span", { className: "text-slate-500", children: [queued, " queued"] }), failed > 0 && _jsxs("span", { className: "text-red-500 font-medium", children: [failed, " failed"] })] }), _jsx("div", { className: "bg-white border border-slate-200 rounded-lg px-4 py-2", children: backupIds.map((id) => (_jsx(BackupProgressCard, { backupId: id }, id))) }), allDone && (_jsxs("div", { className: "mt-6 flex gap-3", children: [_jsx(Link, { to: "/backups", className: "rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700", children: "View All Backups" }), _jsx(Link, { to: "/employees", className: "rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50", children: "\u2190 Back to Employees" })] }))] }));
}
